import React, { useEffect, useState } from "react";

import { useTranslation } from "utils";

import { Icon, IconSizes, Typography } from "components";

// Every SoilSight surface that shows this loader already loads the `scenarioAnalysis` namespace.
const STAGE_KEYS = ["checkingSoils", "modellingWeather", "projectingCropGrowth", "calculatingCarbonUptake"];
const STAGE_INTERVAL_MS = 2400;

export type SoilSightLoaderSize = "m" | "l";

// The Icon size scale also sets the stroke weight, so the glyph stays fine-lined at both ring sizes.
const ICON_SIZE_FOR: Record<SoilSightLoaderSize, IconSizes> = { m: "m", l: "xl" };

export type SoilSightLoaderProps = {
  size?: SoilSightLoaderSize;
  dataTestId?: string;
  className?: string;
};

export const SoilSightLoader = ({ size = "l", dataTestId = "soil-sight-loader", className = "" }: SoilSightLoaderProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const [stageIndex, setStageIndex] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => setStageIndex((currentIndex) => (currentIndex + 1) % STAGE_KEYS.length), STAGE_INTERVAL_MS);

    return function stopCyclingLoadingStages() {
      window.clearInterval(interval);
    };
  }, []);

  return (
    <div className={`soil-sight-loader soil-sight-loader--${size} ${className}`.trim()} data-testid={dataTestId}>
      <span className="soil-sight-loader__mark" aria-hidden="true">
        <svg className="soil-sight-loader__ring" viewBox="0 0 100 100" focusable="false">
          {/* 341 = 11 x (18 + 13), so the dash pattern closes with no seam at whatever radius the ring renders at. */}
          <circle className="soil-sight-loader__ring-track" cx="50" cy="50" r="47" pathLength={341} strokeDasharray="18 13" />
        </svg>
        <Icon iconName="klim/plant-ai" size={ICON_SIZE_FOR[size]} className="soil-sight-loader__icon" />
      </span>
      <div className="soil-sight-loader__status" role="status" aria-live="polite">
        {/* We key on the stage, so the message remounts and replays its entry animation. */}
        <Typography key={stageIndex} variant="paragraph-m" className="soil-sight-loader__message">
          {t(`soilSightLoading.${STAGE_KEYS[stageIndex]}`)}
        </Typography>
      </div>
    </div>
  );
};
